import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import * as Neutralino from '@neutralinojs/lib';
import { syncExecuteRemote, syncBatchExecuteRemote, ensureTursoSchema, selectRemoteDirect, getTursoClient, getTursoStatus } from './tursoSync';
import { initOutbox, enqueueWrite, enqueueBatchWrite } from './syncOutbox';
import { isDesktop } from '../utils/platform';
import { SCHEMA, MIGRATIONS } from './schema';
import { filterPendingMigrationsSync } from './migrationUtils';

export { SCHEMA, MIGRATIONS };


export function runNotasGeneralesFkMigration(db: any) {
  try {
    const fkCheck = db.prepare("PRAGMA foreign_key_list(notas_generales)");
    let hasFk = false;
    while (fkCheck.step()) {
      hasFk = true;
    }
    fkCheck.free();

    if (!hasFk) {
      db.run("PRAGMA foreign_keys = OFF;");
      db.run(`
        CREATE TABLE IF NOT EXISTS notas_generales_new (
            id TEXT PRIMARY KEY,
            titulo TEXT NOT NULL,
            contenido TEXT NOT NULL,
            carpeta_id TEXT,
            asignatura_id TEXT,
            evento_id TEXT,
            tags TEXT,
            en_papelera INTEGER DEFAULT 0,
            fecha_creacion TEXT NOT NULL,
            fecha_modificacion TEXT NOT NULL,
            FOREIGN KEY(asignatura_id) REFERENCES asignaturas(id) ON DELETE SET NULL,
            FOREIGN KEY(carpeta_id) REFERENCES carpetas_notas(id) ON DELETE SET NULL,
            FOREIGN KEY(evento_id) REFERENCES eventos_calendario(id) ON DELETE SET NULL
        );
      `);
      db.run(`
        INSERT OR IGNORE INTO notas_generales_new (id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion)
        SELECT id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion FROM notas_generales;
      `);
      db.run("DROP TABLE notas_generales;");
      db.run("ALTER TABLE notas_generales_new RENAME TO notas_generales;");
      db.run("CREATE INDEX IF NOT EXISTS idx_notas_asignatura ON notas_generales(asignatura_id);");
      db.run("CREATE INDEX IF NOT EXISTS idx_notas_carpeta ON notas_generales(carpeta_id);");
      db.run("PRAGMA foreign_keys = ON;");
    }
  } catch (e) {
    console.warn("[Migración DB] Omitida o ya aplicada FK en notas_generales:", e);
  }
}

function getLocalTableColumns(db: any, tableName: string): Set<string> {
  const columns = new Set<string>();
  try {
    const stmt = db.prepare(`PRAGMA table_info(${tableName})`);
    while (stmt.step()) {
      const row = stmt.getAsObject();
      if (row.name) {
        columns.add(String(row.name).toLowerCase());
      }
    }
    stmt.free();
  } catch (_) {}
  return columns;
}

function runSafeMigrations(db: any) {
  db.run("PRAGMA foreign_keys = ON;");
  initOutbox(db);
  db.run(SCHEMA);
  runNotasGeneralesFkMigration(db);
  const safeAlter = (cmd: string) => { try { db.run(cmd); } catch (_) {} };
  const pending = filterPendingMigrationsSync(MIGRATIONS, (table) => getLocalTableColumns(db, table));
  for (const stmt of pending) {
    safeAlter(stmt);
  }
  safeAlter("UPDATE carreras SET malla_precargada = 1 WHERE id = 'carrera_psico_lic';");
  safeAlter("UPDATE carreras SET creditos_totales = 225 WHERE id = 'carrera_fcea_tea';");
  safeAlter("UPDATE facultades SET sigla = 'PSICO' WHERE id = 'psico';");

  // ── Migración: aplazos JSON → historial_academico ──
  // Se ejecuta una sola vez: si la tabla historial_academico está vacía y existen
  // filas en asignaturas con aplazos no vacíos, se migran los datos.
  try {
    const countStmt = db.prepare("SELECT COUNT(*) as c FROM historial_academico");
    countStmt.step();
    const histCount = countStmt.getAsObject().c;
    countStmt.free();

    if (histCount === 0) {
      const stmt = db.prepare("SELECT id, aplazos FROM asignaturas WHERE aplazos IS NOT NULL AND aplazos != '[]' AND aplazos != ''");
      while (stmt.step()) {
        const row = stmt.getAsObject();
        try {
          const aplazos: (number | null)[] = JSON.parse(row.aplazos as string);
          aplazos.forEach((nota, idx) => {
            const regId = `migrated_${row.id}_${idx}`;
            db.run(
              "INSERT OR IGNORE INTO historial_academico (id, asignatura_id, fecha, instancia, resultado, calificacion) VALUES (?, ?, ?, ?, ?, ?)",
              [regId, row.id, '', 'examen_libre', 'reprobado', nota]
            );
          });
        } catch (_) { /* JSON parse error: skip */ }
      }
      stmt.free();
    }
  } catch (_) { /* tabla no existe aún: se creará en el siguiente run */ }

  // ── Seeding Facultades ──
  try {
    const fCountStmt = db.prepare("SELECT COUNT(*) as c FROM facultades");
    fCountStmt.step();
    const fCount = fCountStmt.getAsObject().c;
    fCountStmt.free();

    if (fCount === 0) {
      db.run(
        "INSERT OR IGNORE INTO facultades (id, nombre, sigla, disponible, escala_calificacion_id, terminologia, design_tokens_ref) VALUES (?, ?, ?, ?, ?, ?, ?)",
        ['fcea', 'Facultad de Ciencias Económicas y de Administración', 'FCEA', 1, 'escala_fcea_2025', JSON.stringify({ materia: 'Asignatura', previa: 'Previatura' }), 'design-fcea.md']
      );

      db.run(
        "INSERT OR IGNORE INTO facultades (id, nombre, sigla, disponible, escala_calificacion_id, terminologia, design_tokens_ref) VALUES (?, ?, ?, ?, ?, ?, ?)",
        ['psico', 'Facultad de Psicología', 'PSICO', 1, 'escala_psico_default', JSON.stringify({ materia: 'UCO', previa: 'Previatura' }), 'design-psico.md']
      );

      const deshabilitadas = [
        { id: 'fing', nombre: 'Facultad de Ingeniería', sigla: 'FING' },
        { id: 'fmed', nombre: 'Facultad de Medicina', sigla: 'FMED' },
        { id: 'fderecho', nombre: 'Facultad de Derecho', sigla: 'Derecho' },
        { id: 'fquim', nombre: 'Facultad de Química', sigla: 'FQUIM' }
      ];

      for (const fac of deshabilitadas) {
        db.run(
          "INSERT OR IGNORE INTO facultades (id, nombre, sigla, disponible, escala_calificacion_id, terminologia, design_tokens_ref) VALUES (?, ?, ?, ?, ?, ?, ?)",
          [fac.id, fac.nombre, fac.sigla, 0, null, JSON.stringify({ materia: 'Materia', previa: 'Previatura' }), null]
        );
      }
    }
  } catch (_) {}

  // ── Seeding Carreras ──
  try {
    const cCountStmt = db.prepare("SELECT COUNT(*) as c FROM carreras");
    cCountStmt.step();
    const cCount = cCountStmt.getAsObject().c;
    cCountStmt.free();

    if (cCount === 0) {
      const carrerasInit = [
        { id: 'carrera_fcea_tea', facultad_id: 'fcea', nombre: 'Tecnicatura en Administración', tipo: 'tecnicatura', creditos_totales: 225, duracion_semestres: 5, locacion: 'Montevideo', malla_precargada: 1 },
        { id: 'carrera_fcea_cp', facultad_id: 'fcea', nombre: 'Contador Público', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
        { id: 'carrera_fcea_la', facultad_id: 'fcea', nombre: 'Licenciatura en Administración', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
        { id: 'carrera_fcea_le', facultad_id: 'fcea', nombre: 'Licenciatura en Economía', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
        { id: 'carrera_psico_lic', facultad_id: 'psico', nombre: 'Licenciatura en Psicología', tipo: 'licenciatura', creditos_totales: 320, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 1 }
      ];

      for (const car of carrerasInit) {
        db.run(
          "INSERT OR IGNORE INTO carreras (id, facultad_id, nombre, tipo, creditos_totales, duracion_semestres, locacion, malla_precargada) VALUES (?, ?, ?, ?, ?, ?, ?, ?)",
          [car.id, car.facultad_id, car.nombre, car.tipo, car.creditos_totales, car.duracion_semestres, car.locacion, car.malla_precargada]
        );
      }
    }
  } catch (_) {}
}



export interface Database {
  select<T>(query: string, params?: any[]): Promise<T>;
  execute(query: string, params?: any[]): Promise<void>;
  executeBatch(statements: Array<{ query: string; params?: any[] }>): Promise<void>;
}

let dbWrapper: Database | null = null;
let sqlDb: any = null;
let rootPath: string = '';
export function getRootPath(): string {
  return rootPath;
}

export async function changeRootPath(): Promise<string | null> {
  if (!isNeutralino) return null;
  try {
    const selected = await Neutralino.os.showFolderDialog("Seleccione el nuevo Directorio Principal (ROOT_DIR)");
    if (selected) {
      rootPath = selected;
      const dataPath = await Neutralino.os.getPath('data');
      const configDir = `${dataPath}/fcea-tracker`;
      const configFilePath = `${configDir}/config.json`;
      await ensureDir(configDir);
      await Neutralino.filesystem.writeFile(configFilePath, JSON.stringify({ rootDir: rootPath }));
      await persistDbAtomically();
      return selected;
    }
  } catch (err) {
    console.error("Error al cambiar el ROOT_DIR:", err);
  }
  return null;
}
let isNeutralino = false;

// Check if running inside Neutralino
if (typeof window !== 'undefined' && ((window as any).Neutralino !== undefined || (window as any).NL_PORT !== undefined)) {
  isNeutralino = true;
}

let saveTimeout: any = null;

async function persistDbAtomically() {
  const isNeu = typeof window !== 'undefined' && ((window as any).Neutralino !== undefined || (window as any).NL_PORT !== undefined);
  if ((!isNeu && !isNeutralino) || !sqlDb || !rootPath) return;
  try {
    const data = sqlDb.export();
    const tempPath = `${rootPath}/fcea-tracker.db.tmp`;
    const finalPath = `${rootPath}/fcea-tracker.db`;
    
    // Write binary file to temp path
    await Neutralino.filesystem.writeBinaryFile(tempPath, data.buffer);
    
    // Move to final path (atomic rename)
    await (Neutralino.filesystem as any).move(tempPath, finalPath);
    console.log("Base de datos guardada atómicamente.");
  } catch (error) {
    console.error("Error al persistir la base de datos atómicamente:", error);
  }
}

function triggerSave() {
  const isNeu = typeof window !== 'undefined' && ((window as any).Neutralino !== undefined || (window as any).NL_PORT !== undefined);
  if (!isNeu && !isNeutralino) return;
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(async () => {
    await persistDbAtomically();
  }, 1500);
}

// Ensure dir helper
async function ensureDir(path: string) {
  try {
    await Neutralino.filesystem.createDirectory(path);
  } catch (err) {
    // Ignore if directory already exists
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
    promise
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

let dbPromise: Promise<Database> | null = null;

export function getDb(): Promise<Database> {
  if (dbPromise) return dbPromise;
  dbPromise = (async () => {
    const initOptions = {
      locateFile: (file: string) => {
        if (typeof process !== 'undefined' && process.env && (process.env.VITEST || process.env.NODE_ENV === 'test')) {
          return `./node_modules/sql.js/dist/${file}`;
        }
        return sqlWasmUrl;
      }
    };

    const initSqlJs = (await import('sql.js')).default;
    const SQL = await initSqlJs(initOptions);

    if (isNeutralino) {
      try {
        // Listen to window close to persist immediately
        try {
          Neutralino.events.on('windowClose', async () => {
            if (saveTimeout) clearTimeout(saveTimeout);
            await persistDbAtomically();
            Neutralino.app.exit();
          });
        } catch (evtErr) {
          console.warn("No se pudo registrar listener windowClose:", evtErr);
        }
        
        // Listen to tab hide/visibilitychange to save data
        document.addEventListener('visibilitychange', async () => {
          if (document.visibilityState === 'hidden') {
            await persistDbAtomically();
          }
        });

        const dataPath = await withTimeout(
          Neutralino.os.getPath('data'),
          3500,
          "Timeout al conectar con Neutralino OS API"
        );
        const configDir = `${dataPath}/fcea-tracker`;
        const configFilePath = `${configDir}/config.json`;
        
        // Read config.json
        try {
          const configContent = await withTimeout(
            Neutralino.filesystem.readFile(configFilePath),
            2000,
            "Timeout al leer config.json"
          );
          const config = JSON.parse(configContent);
          rootPath = config.rootDir || '';
        } catch (err) {
          // Config doesn't exist or timed out
        }

        // If no rootPath, prompt user
        if (!rootPath) {
          rootPath = await withTimeout(
            Neutralino.os.showFolderDialog("Seleccione la carpeta raíz (ROOT_DIR) para Malla"),
            30000,
            "Timeout al mostrar cuadro de diálogo"
          );
          if (!rootPath) {
            throw new Error("No se seleccionó una carpeta raíz para el almacenamiento.");
          }
          await ensureDir(configDir);
          await Neutralino.filesystem.writeFile(configFilePath, JSON.stringify({ rootDir: rootPath }));
        }

        const dbPath = `${rootPath}/fcea-tracker.db`;

        // Read db file
        try {
          const dbData = await withTimeout(
            Neutralino.filesystem.readBinaryFile(dbPath),
            5000,
            "Timeout al leer base de datos"
          );
          sqlDb = new SQL.Database(new Uint8Array(dbData));
          runSafeMigrations(sqlDb);
          console.log("Base de datos cargada exitosamente.");
        } catch (dbErr) {
          console.log("No se encontró base de datos en disk, creando una nueva.");
          sqlDb = new SQL.Database();
          runSafeMigrations(sqlDb);
          // Save initial database
          await persistDbAtomically();
        }



      } catch (err) {
        console.error("Fallo la inicialización de Neutralino, cayendo a base de datos en memoria:", err);
        sqlDb = new SQL.Database();
        runSafeMigrations(sqlDb);
      }
    } else {
      // Fallback for Vitest/Node environment
      sqlDb = new SQL.Database();
      runSafeMigrations(sqlDb);
    }


    dbWrapper = {
    async select<T>(query: string, params?: any[]): Promise<T> {
      // Si estamos en entorno Web y Turso esta conectado, consultar directamente a Turso
      if (!isDesktop() && getTursoClient()) {
        return selectRemoteDirect<T>(query, params);
      }

      // En Desktop o fallback Web en memoria, consultar a sqlDb (sql.js)
      const stmt = sqlDb.prepare(query);
      if (params && params.length > 0) {
        const bindObj: Record<string, any> = {};
        params.forEach((val, i) => {
          bindObj[`$${i + 1}`] = val;
        });
        stmt.bind(bindObj);
      }
      const results: any[] = [];
      while (stmt.step()) {
        results.push(stmt.getAsObject());
      }
      stmt.free();
      return results as unknown as T;
    },

    async execute(query: string, params?: any[]): Promise<void> {
      // Ejecutar síncronamente en memoria sql.js
      const stmt = sqlDb.prepare(query);
      if (params && params.length > 0) {
        const bindObj: Record<string, any> = {};
        params.forEach((val, i) => {
          bindObj[`$${i + 1}`] = val;
        });
        stmt.bind(bindObj);
      }
      stmt.step();
      stmt.free();
      
      if (isDesktop()) {
        triggerSave();
      }

      // Sincronización / Ejecución remota en Turso (asíncrona)
      syncExecuteRemote(query, params || [])
        .then(success => {
          if (!success) {
            const status = getTursoStatus();
            if (status !== 'synced' && status !== 'disabled') {
              enqueueWrite(query, params || []);
            }
          }
        })
        .catch(err => {
          console.warn("Sync en segundo plano a Turso omitido o fallido:", err);
          const status = getTursoStatus();
          if (status !== 'synced' && status !== 'disabled') {
            enqueueWrite(query, params || []);
          }
        });
    },

    async executeBatch(statements: Array<{ query: string; params?: any[] }>): Promise<void> {
      if (!statements || statements.length === 0) return;

      sqlDb.exec("BEGIN TRANSACTION;");
      try {
        for (const item of statements) {
          const stmt = sqlDb.prepare(item.query);
          if (item.params && item.params.length > 0) {
            const bindObj: Record<string, any> = {};
            item.params.forEach((val, i) => {
              bindObj[`$${i + 1}`] = val;
            });
            stmt.bind(bindObj);
          }
          stmt.step();
          stmt.free();
        }
        sqlDb.exec("COMMIT;");
      } catch (err) {
        sqlDb.exec("ROLLBACK;");
        throw err;
      }

      if (isDesktop()) {
        triggerSave();
      }

      const remoteStatements = statements.map(s => ({ sql: s.query, args: s.params || [] }));
      syncBatchExecuteRemote(remoteStatements)
        .then(success => {
          if (!success) {
            const status = getTursoStatus();
            if (status !== 'synced' && status !== 'disabled') {
              enqueueBatchWrite(remoteStatements);
            }
          }
        })
        .catch((err: any) => {
          console.warn("Sync batch en segundo plano a Turso omitido o fallido:", err);
          const status = getTursoStatus();
          if (status !== 'synced' && status !== 'disabled') {
            enqueueBatchWrite(remoteStatements);
          }
        });
    }
  };

  // Asegurar esquema en la nube en segundo plano al iniciar
  ensureTursoSchema(SCHEMA).catch(() => {});

  return dbWrapper;
})();
  return dbPromise;
}

export interface MallaBackupData {
  version: string;
  exportDate: string;
  facultades?: any[];
  carreras?: any[];
  planes_estudio?: any[];
  modulos?: any[];
  ciclos?: any[];
  asignaturas?: any[];
  evaluaciones?: any[];
  materiales?: any[];
  carpetas_notas?: any[];
  notas_generales?: any[];
  horarios_clases?: any[];
  eventos_calendario?: any[];
  historial_academico?: any[];
  acreditaciones_libres?: any[];
}

export async function exportBackupData(): Promise<MallaBackupData> {
  const db = await getDb();
  const tables = [
    'facultades', 'carreras', 'planes_estudio', 'modulos', 'ciclos',
    'asignaturas', 'evaluaciones', 'materiales', 'carpetas_notas',
    'notas_generales', 'horarios_clases', 'eventos_calendario',
    'historial_academico', 'acreditaciones_libres'
  ];

  const backup: Record<string, any> = {
    version: '2.0.0',
    exportDate: new Date().toISOString()
  };

  for (const table of tables) {
    try {
      const rows = await db.select<any[]>(`SELECT * FROM ${table}`);
      backup[table] = rows || [];
    } catch (_) {
      backup[table] = [];
    }
  }

  return backup as MallaBackupData;
}

export async function importBackupData(backupData: MallaBackupData): Promise<{ success: boolean; importedCount: number }> {
  if (!backupData || typeof backupData !== 'object') {
    throw new Error('Formato de copia de seguridad no válido.');
  }

  const db = await getDb();
  const tables = [
    'facultades', 'carreras', 'planes_estudio', 'modulos', 'ciclos',
    'asignaturas', 'acreditaciones_libres', 'evaluaciones', 'materiales',
    'carpetas_notas', 'notas_generales', 'horarios_clases', 'eventos_calendario',
    'historial_academico'
  ];

  const batchStatements: Array<{ query: string; params: any[] }> = [];
  let totalImported = 0;

  for (const table of tables) {
    const rows = (backupData as any)[table];
    if (Array.isArray(rows) && rows.length > 0) {
      for (const row of rows) {
        try {
          const keys = Object.keys(row);
          if (keys.length === 0) continue;
          const placeholders = keys.map((_, i) => `$${i + 1}`).join(', ');
          const values = Object.values(row).map(val => (typeof val === 'object' && val !== null ? JSON.stringify(val) : val));
          const sql = `INSERT OR REPLACE INTO ${table} (${keys.join(', ')}) VALUES (${placeholders})`;
          batchStatements.push({ query: sql, params: values });
          totalImported++;
        } catch (_) {}
      }
    }
  }

  if (batchStatements.length > 0) {
    await db.executeBatch(batchStatements);
  }

  return { success: true, importedCount: totalImported };
}

